import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useInlineCompletion - Hook for managing inline text completions (ghost text)
 *
 * Provides real-time typing suggestions that appear as ghost text.
 * Users can press Tab to accept the suggestion.
 *
 * @param {Object} options
 * @param {string} options.text - Current text content
 * @param {number} options.cursorPosition - Current cursor position
 * @param {boolean} options.enabled - Whether completions are enabled
 * @param {number} options.debounceMs - Debounce delay before showing suggestion (default: 500)
 * @param {function} options.onComplete - Optional callback for AI completions
 */

// Common email phrase completions (pattern matching - no API needed)
const PATTERN_COMPLETIONS = {
  // Greetings
  'Hi ': ['there,', 'all,'],
  'Hello ': ['there,', 'everyone,'],
  'Hey ': ['there,'],
  'Good ': ['morning,', 'afternoon,', 'evening,'],
  'Dear ': ['Team,', 'All,'],

  // Common starters
  'I hope ': ['this email finds you well.', "you're doing well.", 'all is well.'],
  'I wanted to ': [
    'follow up on our conversation.',
    'reach out about',
    'check in on',
    'let you know that',
    'thank you for',
  ],
  'I am writing ': ['to follow up on', 'to inquire about', 'regarding', 'to confirm'],
  "I'm writing ": ['to follow up on', 'to inquire about', 'regarding', 'to confirm'],
  'Thank you ': ['for your time.', 'for reaching out.', 'for your response.', 'for considering my request.'],
  'Thanks ': ['for your time.', 'for reaching out.', 'for your response.', 'so much!', 'again!'],
  'I appreciate ': ['your time.', 'your help.', 'your understanding.', 'you getting back to me.'],
  'Just wanted ': ['to follow up on', 'to check in about', 'to remind you about', 'to let you know'],
  'Following up ': ['on our conversation.', 'on my previous email.', 'on the meeting.'],
  'As discussed ': ['earlier,', 'in our meeting,', 'on the call,'],
  'As per ': ['our conversation,', 'your request,', 'the agreement,'],
  'Please let ': ['me know if you have any questions.', 'me know your thoughts.', 'us know how we can help.'],
  'Please feel ': ['free to reach out if you have any questions.', 'free to contact me.'],

  // Professional phrases
  'Looking forward ': ['to hearing from you.', 'to your response.', 'to connecting.', 'to meeting you.'],
  'I look forward ': ['to hearing from you.', 'to your response.', 'to our meeting.', 'to working with you.'],
  'Would you be ': ['available for a call?', 'able to meet this week?', 'interested in'],
  'Could you ': ['please confirm', 'let me know', 'share your thoughts on'],
  'I would be ': ['happy to discuss this further.', 'glad to help.', 'available to meet.'],
  "I'd be ": ['happy to discuss this further.', 'glad to help.', 'available to meet.'],
  'Let me know ': ['if you have any questions.', 'your thoughts.', 'if this works for you.'],
  'Feel free ': ['to reach out if you have any questions.', 'to contact me.', 'to let me know.'],

  // Closings
  'Best ': ['regards,', 'wishes,'],
  'Kind ': ['regards,'],
  'Warm ': ['regards,'],
  'Sincerely ': ['yours,'],
  'Talk ': ['soon!', 'soon,'],
  'Take ': ['care!', 'care,'],
  'Have a ': ['great day!', 'wonderful day!', 'nice weekend!'],

  // Action items
  'Please ': ['let me know', 'find attached', 'see the attached', 'review and', 'confirm'],
  'Attached ': ['is the document you requested.', 'please find the', 'are the files for'],
  "I've attached ": ['the document for your review.', 'the files you requested.'],

  // Time-related
  'At your ': ['earliest convenience.', 'convenience,'],
  'By the ': ['end of the day.', 'end of the week.', 'end of the month.'],

  // Meeting-related
  'Would ': ['tomorrow work for you?', 'you be available', 'you have time to'],
  'Are you ': ['available to meet', 'free for a call', 'around this week'],
  'Can we ': ['schedule a call?', 'set up a meeting?', 'discuss this further?'],
  "Let's ": ['schedule a call.', 'connect soon.', 'discuss this.', 'set up a meeting.'],
};

const useInlineCompletion = ({
  text = '',
  cursorPosition = 0,
  enabled = true,
  debounceMs = 400,
  onComplete,
}) => {
  const [suggestion, setSuggestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const lastTextRef = useRef('');

  // Get text before cursor
  const getTextBeforeCursor = useCallback(() => {
    if (cursorPosition <= 0) return '';
    return text.slice(0, cursorPosition);
  }, [text, cursorPosition]);

  // Find pattern match completion
  const findPatternCompletion = useCallback((textBeforeCursor) => {
    if (!textBeforeCursor) return null;

    // Get the last part of text (current sentence/phrase)
    const lines = textBeforeCursor.split('\n');
    const currentLine = lines[lines.length - 1];

    // Try to match patterns starting from longer matches
    const sortedPatterns = Object.keys(PATTERN_COMPLETIONS).sort((a, b) => b.length - a.length);

    for (const pattern of sortedPatterns) {
      if (currentLine.endsWith(pattern)) {
        const completions = PATTERN_COMPLETIONS[pattern];
        // Return the first completion (most common)
        return completions[0];
      }
    }

    return null;
  }, []);

  // Generate completion
  const generateCompletion = useCallback(async () => {
    if (!enabled) {
      setSuggestion('');
      return;
    }

    const textBeforeCursor = getTextBeforeCursor();

    // Don't generate if text is too short or hasn't changed
    if (textBeforeCursor.length < 3 || textBeforeCursor === lastTextRef.current) {
      return;
    }

    lastTextRef.current = textBeforeCursor;

    // First try pattern matching (instant, no API)
    const patternMatch = findPatternCompletion(textBeforeCursor);
    if (patternMatch) {
      setSuggestion(patternMatch);
      return;
    }

    // If no pattern match and we have an API completion function, use it
    if (onComplete) {
      setIsLoading(true);
      try {
        const completion = await onComplete(textBeforeCursor);
        if (completion && typeof completion === 'string') {
          setSuggestion(completion);
        } else {
          setSuggestion('');
        }
      } catch (error) {
        console.error('Error generating completion:', error);
        setSuggestion('');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSuggestion('');
    }
  }, [enabled, getTextBeforeCursor, findPatternCompletion, onComplete]);

  // Debounced completion generation
  useEffect(() => {
    if (!enabled) {
      setSuggestion('');
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout
    debounceRef.current = setTimeout(() => {
      generateCompletion();
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [text, cursorPosition, enabled, debounceMs, generateCompletion]);

  // Clear suggestion when text changes significantly
  useEffect(() => {
    const textBeforeCursor = getTextBeforeCursor();

    // If the text no longer ends with what we expected, clear suggestion
    if (suggestion && !textBeforeCursor.endsWith(lastTextRef.current.slice(-20))) {
      setSuggestion('');
    }
  }, [text, suggestion, getTextBeforeCursor]);

  // Accept the current suggestion
  const acceptSuggestion = useCallback(() => {
    const result = suggestion;
    setSuggestion('');
    return result;
  }, [suggestion]);

  // Dismiss the current suggestion
  const dismissSuggestion = useCallback(() => {
    setSuggestion('');
  }, []);

  return {
    suggestion,
    isLoading,
    acceptSuggestion,
    dismissSuggestion,
    hasSuggestion: suggestion.length > 0,
  };
};

export default useInlineCompletion;
